import React, { PureComponent } from 'react';

import { debounce } from 'lodash-es';
import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';

import { ComponentLinkProxy } from 'src/components/component-link';
import SearchField from 'src/components/search-field';
import urls from 'src/sections/repository/urls';
import { publishUiEvent } from 'src/utils/analytics/publish';

import RefSelector from '../containers/ref-selector';
import { Filter } from '../types';

import messages from './filters.i18n';
import * as styles from './filters.style';

type Props = {
  intl: InjectedIntl;
  repositoryFullSlug: string;
  refName?: string;
  filter: Filter;
  onFilterChange: (filter: Partial<Filter>) => void;
  onClickShowAll?: () => void;
};

type State = {
  currentSearch: string | null | undefined;
  lastSearch: string | null | undefined;
};

export const DEBOUNCE_TIME = 400;

class Filters extends PureComponent<Props, State> {
  state = {
    currentSearch: this.props.filter.search,
    lastSearch: this.props.filter.search,
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (prevState.lastSearch !== nextProps.filter.search) {
      return {
        currentSearch: nextProps.filter.search,
        lastSearch: nextProps.filter.search,
      };
    }
    return null;
  }

  debouncedSearchChange = debounce((search: string) => {
    this.props.onFilterChange({ search });
    publishUiEvent({
      action: 'changed',
      actionSubject: 'search',
      actionSubjectId: 'commitsSearch',
      source: 'commitsScreen',
    });
  }, DEBOUNCE_TIME);

  handleRefSelectorChanged = () => {
    this.setState({ currentSearch: '' });
    publishUiEvent({
      action: 'changed',
      actionSubject: 'option',
      actionSubjectId: 'branchSelect',
      source: 'commitsScreen',
    });
  };

  handleShowAllCommitsButtonClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'showAllCommitsButton',
      source: 'commitsScreen',
    });
    if (this.props.onClickShowAll) {
      this.props.onClickShowAll();
    }
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  handleSearchChange = e => {
    this.setState({ currentSearch: e.target.value }, () =>
      this.debouncedSearchChange(this.state.currentSearch.trim())
    );
  };

  notifyFilterChanged = (search: string) => {
    this.props.onFilterChange({ search });
    publishUiEvent({
      action: 'changed',
      actionSubject: 'search',
      actionSubjectId: 'commitsSearch',
      source: 'commitsScreen',
    });
  };

  clearFilter = () => {
    const currentSearch = '';
    this.setState({ currentSearch });
    this.notifyFilterChanged(currentSearch);
  };

  render() {
    const { refName, repositoryFullSlug, intl } = this.props;

    return (
      <styles.FilterGroup data-qa="commit-list-filters">
        <SearchField
          currentSearch={this.state.currentSearch}
          handleFilterChange={this.handleSearchChange}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          clearFilter={this.clearFilter}
          fieldName="search-commits-input"
        />
        <RefSelector onChange={this.handleRefSelectorChanged} label={refName} />
        {refName && (
          <CustomThemeButton
            appearance="link"
            href={urls.ui.commits(repositoryFullSlug)}
            component={ComponentLinkProxy}
            onClick={this.handleShowAllCommitsButtonClick}
          >
            <FormattedMessage {...messages.showAllCommitsButton} />
          </CustomThemeButton>
        )}
      </styles.FilterGroup>
    );
  }
}

export default injectIntl(Filters);
